import type { WorldConfig } from "../sim/index";
import type { Snapshot, ToWorker } from "./protocol";
import { reportError } from "../diagnostics";

/**
 * Main-thread handle to the sim worker. Owns the worker lifecycle, forwards
 * plant commands, and hands the latest two snapshots to the renderer for
 * interpolation.
 */
export class SimClient {
  readonly worker: Worker;
  prev: Snapshot | null = null;
  cur: Snapshot | null = null;
  /** Wall-clock ms when `cur` arrived, for interpolation. */
  curArrivedAt = 0;

  constructor(config: WorldConfig) {
    this.worker = new Worker(new URL("./sim.worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (e: MessageEvent<Snapshot>) => {
      this.prev = this.cur;
      this.cur = e.data;
      this.curArrivedAt = performance.now();
    };
    // A worker exception is otherwise a silent freeze (snapshots just stop);
    // surface it so it's diagnosable instead of an invisible "crash".
    this.worker.onerror = (e) => reportError("sim worker", e.message || e);
    this.worker.onmessageerror = () => reportError("sim worker", "message deserialization failed");
    this.post({ type: "init", config });
  }

  plant(species: number, x: number, z: number): void {
    this.post({ type: "plant", species, x, z });
  }

  setSpeed(speed: number): void {
    this.post({ type: "speed", speed });
  }

  dispose(): void {
    this.worker.terminate();
  }

  private post(msg: ToWorker): void {
    this.worker.postMessage(msg);
  }
}
